<template>
  <div class="profile-container">
    <el-card class="profile-card">
      <div slot="header" class="card-header">
        <span><i class="el-icon-user"></i>个人信息</span>
      </div>

      <el-row :gutter="40">
        <!-- 左侧头像区域 -->
        <el-col :span="8">
          <div class="avatar-section">
            <div class="avatar-wrapper">
              <el-avatar :src="form.avatar || defaultAvatar" :size="150"></el-avatar>
              <div class="avatar-overlay">
                <el-upload
                  class="avatar-uploader"
                  action="/api/upload/avatar"
                  :headers="uploadHeaders"
                  :show-file-list="false"
                  :on-success="handleAvatarSuccess"
                  :before-upload="beforeAvatarUpload">
                  <i class="el-icon-camera avatar-uploader-icon"></i>
                  <div class="upload-text">上传头像</div>
                </el-upload>
              </div>
            </div>
            <div class="avatar-tip">
              <p>支持 JPG、PNG 格式</p>
              <p>文件大小不超过 2MB</p>
            </div>
          </div>
        </el-col>

        <!-- 右侧表单区域 -->
        <el-col :span="16">
          <el-form :model="form" :rules="rules" ref="profileForm" label-width="100px" class="profile-form">
            <el-form-item label="用户名">
              <el-input v-model="form.username" disabled>
                <i slot="prefix" class="el-icon-user"></i>
              </el-input>
              <div class="tip">用户名不可修改</div>
            </el-form-item>

            <el-form-item label="昵称" prop="nickname">
              <el-input v-model="form.nickname" placeholder="请输入昵称">
                <i slot="prefix" class="el-icon-star-off"></i>
              </el-input>
            </el-form-item>

            <el-form-item label="邮箱" prop="email">
              <el-input v-model="form.email" placeholder="请输入邮箱">
                <i slot="prefix" class="el-icon-message"></i>
              </el-input>
            </el-form-item>

            <el-form-item label="积分">
              <el-input v-model="form.points" disabled>
                <i slot="prefix" class="el-icon-coin"></i>
              </el-input>
              <div class="tip">积分由系统自动计算</div>
            </el-form-item>

            <el-form-item label="个人描述" prop="description">
              <el-input
                type="textarea"
                v-model="form.description"
                :rows="4"
                maxlength="255"
                show-word-limit
                placeholder="介绍一下自己吧">
              </el-input>
            </el-form-item>

            <el-form-item label="注册时间">
              <el-input v-model="formattedCreateTime" disabled>
                <i slot="prefix" class="el-icon-time"></i>
              </el-input>
            </el-form-item>

            <el-form-item>
              <el-button type="primary" @click="handleSave" :loading="saving">
                <i class="el-icon-check"></i> 保存修改
              </el-button>
              <el-button @click="handleReset">
                <i class="el-icon-refresh"></i> 重置
              </el-button>
            </el-form-item>
          </el-form>
        </el-col>
      </el-row>
    </el-card>
  </div>
</template>

<script>
export default {
  name: 'AdminProfile',
  data() {
    return {
      form: {
        id: null,
        username: '',
        nickname: '',
        email: '',
        avatar: '',
        points: 0,
        description: '',
        createTime: ''
      },
      defaultAvatar: 'https://cube.elemecdn.com/3/7c/3ea6beec64369c2642b92c6726f1epng.png',
      originalForm: {},
      saving: false,
      uploadHeaders: {
        Authorization: `Bearer ${localStorage.getItem('admin_token')}`
      },
      rules: {
        nickname: [
          { required: true, message: '请输入昵称', trigger: 'blur' },
          { min: 2, max: 50, message: '昵称长度在 2 到 50 个字符', trigger: 'blur' }
        ],
        email: [
          { type: 'email', message: '请输入正确的邮箱地址', trigger: 'blur' }
        ],
        description: [
          { max: 255, message: '个人描述不能超过 255 个字符', trigger: 'blur' }
        ]
      }
    }
  },
  computed: {
    formattedCreateTime() {
      if (!this.form.createTime) return ''
      return new Date(this.form.createTime).toLocaleString('zh-CN')
    }
  },
  mounted() {
    this.loadUserInfo()
  },
  methods: {
    async loadUserInfo() {
      try {
        const res = await this.$axios.get('/admin/user/profile')
        if (res.data.code === 200) {
          this.form = { ...res.data.data }
          this.originalForm = { ...res.data.data }
        }
      } catch (error) {
        this.$message.error('加载用户信息失败')
      }
    },
    async handleSave() {
      this.$refs.profileForm.validate(async (valid) => {
        if (!valid) return

        this.saving = true
        try {
          const res = await this.$axios.put('/admin/user/profile', {
            nickname: this.form.nickname,
            email: this.form.email,
            description: this.form.description
          })

          if (res.data.code === 200) {
            this.$message.success('保存成功')
            this.originalForm = { ...this.form }
            
            // 更新 localStorage 中的用户信息
            const userInfo = {
              ...this.form,
              password: null
            }
            localStorage.setItem('admin_user', JSON.stringify(userInfo))
            
            // 触发全局事件，通知 Layout 更新用户信息
            this.$root.$emit('user-info-change', userInfo)
          }
        } catch (error) {
          this.$message.error('保存失败')
        } finally {
          this.saving = false
        }
      })
    },
    handleReset() {
      this.form = { ...this.originalForm }
      this.$refs.profileForm.clearValidate()
    },
    beforeAvatarUpload(file) {
      const isImage = file.type === 'image/jpeg' || file.type === 'image/png'
      const isLt2M = file.size / 1024 / 1024 < 2

      if (!isImage) {
        this.$message.error('头像只能是 JPG 或 PNG 格式!')
        return false
      }
      if (!isLt2M) {
        this.$message.error('头像大小不能超过 2MB!')
        return false
      }
      return true
    },
    async handleAvatarSuccess(response) {
      if (response.code === 200) {
        this.form.avatar = response.data.url
        this.$message.success('头像上传成功')
        
        // 自动保存头像更新
        try {
          await this.$axios.put('/admin/user/avatar', {
            avatar: this.form.avatar
          })
          
          // 更新 localStorage
          const userInfo = JSON.parse(localStorage.getItem('admin_user') || '{}')
          userInfo.avatar = this.form.avatar
          localStorage.setItem('admin_user', JSON.stringify(userInfo))
          
          // 触发全局事件
          this.$root.$emit('user-info-change', userInfo)
        } catch (error) {
          this.$message.error('头像保存失败')
        }
      } else {
        this.$message.error('头像上传失败')
      }
    }
  }
}
</script>

<style scoped>
.profile-container {
  padding: 20px;
  background: transparent;
  min-height: calc(100vh - 60px);
}

.profile-card {
  max-width: 1200px;
  margin: 0 auto;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
}

.card-header {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}

.card-header i {
  margin-right: 8px;
  color: #409EFF;
}

.avatar-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px 0;
}

.avatar-wrapper {
  position: relative;
  width: 150px;
  height: 150px;
  border-radius: 50%;
  overflow: hidden;
  cursor: pointer;
  margin-bottom: 20px;
}

.avatar-wrapper:hover .avatar-overlay {
  opacity: 1;
}

.avatar-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  opacity: 0;
  transition: opacity 0.3s;
}

.avatar-uploader {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.avatar-uploader-icon {
  font-size: 32px;
  color: #fff;
  margin-bottom: 8px;
}

.upload-text {
  color: #fff;
  font-size: 14px;
}

.avatar-tip {
  text-align: center;
  font-size: 12px;
  color: #909399;
  line-height: 1.8;
}

.profile-form {
  padding: 20px 0;
}

.profile-form .el-form-item {
  margin-bottom: 28px;
}

.tip {
  margin-top: 5px;
  font-size: 12px;
  color: #909399;
  line-height: 1.5;
}

/* 深色模式样式 */
.admin-dark-mode .profile-card {
  background-color: #2a2a2a;
  border-color: #404040;
}

.admin-dark-mode .card-header {
  color: #e0e0e0;
}

.admin-dark-mode .tip {
  color: #b0b0b0;
}

.admin-dark-mode .avatar-tip {
  color: #b0b0b0;
}

/* 字数统计组件深色样式 */
.admin-dark-mode .el-input__count {
  background-color: transparent !important;
  color: #b0b0b0 !important;
}

.admin-dark-mode .el-textarea .el-input__count {
  background-color: transparent !important;
  color: #b0b0b0 !important;
}

.admin-dark-mode .el-textarea__inner {
  background-color: #1a1a1a !important;
  border-color: #404040 !important;
  color: #e0e0e0 !important;
}

/* 表单项标签深色样式 */
.admin-dark-mode .el-form-item__label {
  color: #b0b0b0 !important;
}

/* 输入框深色样式 */
.admin-dark-mode .el-input__inner {
  background-color: #1a1a1a !important;
  border-color: #404040 !important;
  color: #e0e0e0 !important;
}

.admin-dark-mode .el-input__inner:disabled {
  background-color: #252525 !important;
  color: #606060 !important;
}

/* 输入框图标深色样式 */
.admin-dark-mode .el-input__prefix {
  color: #909399 !important;
}

/* 按钮深色样式 */
.admin-dark-mode .el-button--default {
  background-color: #2a2a2a !important;
  border-color: #404040 !important;
  color: #b0b0b0 !important;
}

.admin-dark-mode .el-button--default:hover {
  background-color: #323232 !important;
  border-color: #505050 !important;
  color: #e0e0e0 !important;
}
</style>
